import { Workflow, WorkflowSubmission } from '@aero-platform/shared'
import { RunStatus } from '@aero-platform/shared/generated/AeroStructures'
import { Column } from 'primereact/column'
import {
  DataTable,
  DataTableExpandedRows,
  DataTablePageEvent,
  DataTableSortEvent,
  SortOrder
} from 'primereact/datatable'
import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link, NavLink } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { ReactComponent as ViewIcon } from '../../assets/svg/eye.svg'
import IconButton from '../../components/buttons/IconButton'
import { CollapseIcon, ExpandIcon } from '../../components/Icons'
import SubmissionStatusCell from '../../components/submission/SubmissionStatusCell'
import { LazyTableState } from '../../types/types'
import { formatDateWithLocale } from '../../utils/intl'
import { currentUserState } from '../../utils/state-atoms'
import { computeSupersededIds } from '../../types/submission-util'

const pageSizeOptions = [100, 200, 500]

const verifyOverallStatus = (submission: any): RunStatus => {
  const statusPriority: Record<number, number> = {
    [RunStatus.ERROR]: 8,
    [RunStatus.RUNNING]: 7,
    [RunStatus.PENDING]: 6,
    [RunStatus.SUBMITTED]: 5,
    [RunStatus.WAITING]: 4,
    [RunStatus.UNSPECIFIED]: 3,
    [RunStatus.WARN_ON_ERROR]: 2,
    [RunStatus.COMPLETED]: 1,
    [RunStatus.CANCELLED]: 1,
    [RunStatus.CREATED]: 0
  }
  let highestPriority = -1
  let highestStatus = RunStatus.UNSPECIFIED

  submission.submissionRunGroups.forEach((srg: any) => {
    const latestByName: Record<string, string> = {}
    srg.workflowRunGroups?.forEach((wrg: any) => {
      if (wrg.name !== 'All') {
        latestByName[wrg.name] = wrg.systemRunId
      }
    })

    srg.workflowRunGroups.forEach((wrg: any) => {
      if (wrg.name !== 'All' && wrg.systemRunId !== latestByName[wrg.name]) return

      wrg.workflow.forEach((wf: any) => {
        const st = wf.overallStatus ?? RunStatus.UNSPECIFIED
        const pri = statusPriority[st] ?? 0
        if (pri > highestPriority) {
          highestPriority = pri
          highestStatus = st
        }
      })
    })
  })

  return highestStatus === RunStatus.UNSPECIFIED ? RunStatus.CREATED : highestStatus
}

const getErrorFromWorkflow = (wf: Workflow): string => {
  let errorMsg = ''
  wf.workflowTasks.forEach(task => {
    if (task.status.runStatus === RunStatus.ERROR) {
      errorMsg += `${task.status.httpResultText}\n`
    }
  })
  return errorMsg
}

type Props = {
  submissions: WorkflowSubmission[]
  totalRecords: number
  loading: boolean
  selectedSubmissions: WorkflowSubmission[]
  onSelectionChange: (val: WorkflowSubmission[]) => void
  lazyState: LazyTableState
  onPage: (e: DataTablePageEvent) => void
  sortField: string
  sortOrder: SortOrder
  onSort: (e: DataTableSortEvent) => void
  onOpenImages: (payload: {
    systemRunId: string
    runId: string
    caseName: string
    baselineRunId?: string
    ref2RunId?: string
  }) => void
}

const RunView: FC<Props> = ({
  submissions,
  totalRecords,
  loading,
  selectedSubmissions,
  onSelectionChange,
  lazyState,
  onPage,
  sortField,
  sortOrder,
  onSort,
  onOpenImages
}) => {
  const navigate = useNavigate()
  const currentUser = useRecoilValue(currentUserState)

  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({})
  const [openPriorSections, setOpenPriorSections] = useState<Record<string, boolean>>({})

  const columns = [
    {
      header: 'ID',
      field: 'name',
      sortable: true,
      headerClassName: 'w-[200px]',
      bodyClassName: 'w-[200px] id-cell',
      body: rowData => {
        const name = rowData.name
        const rowId = rowData._id
        return (
          <div className="flex items-center justify-between" id="case-name">
            <div
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() => {
                const copyExpandedRows: DataTableExpandedRows = { ...expandedRows }
                if (expandedRows[rowId]) {
                  delete copyExpandedRows[rowId]
                } else {
                  copyExpandedRows[rowId] = true
                }
                setExpandedRows(copyExpandedRows)
              }}>
              {expandedRows[rowId] ? (
                <CollapseIcon className="fill-dark-blue dark:fill-light-blue h-6 w-6" />
              ) : (
                <ExpandIcon className="fill-dark-blue dark:fill-light-blue h-6 w-6" />
              )}
              <Link to={`/submissions/${rowData.name}`}>{name}</Link>
            </div>
          </div>
        )
      }
    },
    {
      header: 'Project',
      field: 'projectName',
      sortable: true,
      headerClassName: 'w-[150px]',
      bodyClassName: 'w-[150px]',
      body: (rowData: WorkflowSubmission) => {
        return (
          <Link to={`/projects/${rowData.projectName}`} className="underline">
            {rowData.projectName}
          </Link>
        )
      }
    },
    {
      header: 'Created',
      field: 'timeCreated',
      headerClassName: 'w-[200px]',
      sortable: true,
      body: rowData => {
        return <span>{formatDateWithLocale(rowData.timeCreated, currentUser?.locale)}</span>
      }
    },
    {
      header: 'Updated',
      field: 'timeUpdated',
      headerClassName: 'w-[200px]',
      sortable: true,
      body: rowData => {
        if (!rowData.timeUpdated && rowData.overallStatus === RunStatus.CREATED) {
          return <span>{formatDateWithLocale(rowData.timeCreated, currentUser?.locale)}</span>
        }
        return <span>{formatDateWithLocale(rowData.timeUpdated, currentUser?.locale)}</span>
      }
    },
    {
      header: 'Submitted By',
      sortable: false,
      headerClassName: 'w-[200px]',
      body: (rowData: WorkflowSubmission & { submittedByName?: string }) => {
        const name =
          (rowData.submitUser &&
            rowData.submitUser.firstName + ' ' + rowData.submitUser.lastName) ||
          ''
        return <span>{name}</span>
      }
    },
    {
      header: 'Overall Status',
      field: 'overallStatus',
      headerClassName: 'w-[200px]',
      sortable: true,
      body: (rowData: WorkflowSubmission) => {
        return <SubmissionStatusCell status={verifyOverallStatus(rowData)} />
      }
    }
  ]

  return (
    <>
      <DataTable
        value={submissions}
        first={lazyState.first}
        totalRecords={totalRecords}
        loading={loading}
        selectionMode="checkbox"
        dragSelection
        selection={selectedSubmissions}
        onSelectionChange={e => onSelectionChange(e.value)}
        sortField={sortField}
        sortOrder={sortOrder}
        dataKey="_id"
        stripedRows
        rows={lazyState.rows}
        onSort={onSort}
        lazy
        paginator
        onPage={onPage}
        rowsPerPageOptions={pageSizeOptions}
        expandedRows={expandedRows}
        isDataSelectable={event => event.data.overallStatus === RunStatus.ERROR}
        rowExpansionTemplate={(rowData: WorkflowSubmission) => {
          // Compute superseding across ALL containers so a per-case resubmit (appended as a
          // separate container) retires the run it replaced. Keys drafting positions by the
          // following car's stable localPosition via getSupersedeCaseKey.
          const supersededIds = computeSupersededIds(
            rowData.submissionRunGroups.flatMap(c => c.workflowRunGroups)
          )
          return (
            <section className="px-8 max-h-80 overflow-auto">
              {rowData.submissionRunGroups.map(runGroup => {
                const activeCases =
                  runGroup?.workflowRunGroups.filter(
                    wrg => wrg.name !== 'All' && !supersededIds.has(wrg.systemRunId)
                  ) || []
                const priorCases =
                  runGroup?.workflowRunGroups.filter(
                    wrg => wrg.name !== 'All' && supersededIds.has(wrg.systemRunId)
                  ) || []
                const priorKey = `${rowData._id}-${runGroup.name}`

                const renderCaseRow = (workflowRunGroup: any, isPrior = false) => (
                  <div
                    className={`grid grid-cols-11 pb-4 border-b border-b-[#e6e6e6] mb-4 ${
                      isPrior ? 'opacity-60' : ''
                    }`}
                    key={workflowRunGroup.systemRunId || workflowRunGroup.name}>
                    <div className="col-span-4 flex px-4 items-start gap-2">
                      <NavLink
                        to={`/submissions/${rowData.name}?case=${workflowRunGroup.systemRunId}`}
                        className="text-gm-blue dark:text-light-blue">
                        <span className="font-semibold">Case:</span>
                        <span className="pl-1">{workflowRunGroup.name}</span>
                      </NavLink>
                      {isPrior && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded">
                          Initial Run
                        </span>
                      )}
                      {!isPrior && (
                        <button
                          title="View Post-Processing Images"
                          onClick={() =>
                            onOpenImages({
                              runId: rowData.name,
                              systemRunId: workflowRunGroup.systemRunId,
                              caseName: workflowRunGroup.name,
                              baselineRunId: rowData.runConfig?.referenceRunName || undefined
                            })
                          }
                          className="p-1 rounded">
                          <i className="pi pi-images text-sm text-gray-600 dark:text-gray-400" />
                        </button>
                      )}
                    </div>
                    <div className="col-span-7 grid grid-cols-8">
                      {workflowRunGroup?.workflow.map((workflow, index) => {
                        return (
                          <div className="col-span-8 grid grid-cols-8" key={index}>
                            <div className="col-span-5 grid grid-cols-5">
                              <span className="pl-4 flex col-span-5 font-semibold">
                                Workflow #{index + 1}
                              </span>
                              <span className="pl-4 flex col-span-5">{workflow?.name}</span>
                              {workflow.overallStatus === RunStatus.ERROR ? (
                                <span className="pl-4 flex col-span-5 text-run-error font-bold whitespace-pre-wrap">
                                  {getErrorFromWorkflow(workflow)}
                                </span>
                              ) : null}
                            </div>
                            <div className="pl-12 ml-12 col-span-3 flex">
                              <SubmissionStatusCell status={workflow.overallStatus} />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )

                return (
                  <div className="grid grid-cols-12" key={runGroup.name}>
                    <div className="col-span-12 pl-10">
                      {activeCases.map(wrg => renderCaseRow(wrg, false))}
                      {priorCases.length > 0 && (
                        <div className="mt-2 mb-4">
                          <div
                            className="flex items-center gap-2 cursor-pointer select-none py-1"
                            onClick={() =>
                              setOpenPriorSections(prev => ({
                                ...prev,
                                [priorKey]: !prev[priorKey]
                              }))
                            }>
                            {openPriorSections[priorKey] ? (
                              <CollapseIcon className="fill-dark-blue dark:fill-light-blue h-4 w-4" />
                            ) : (
                              <ExpandIcon className="fill-dark-blue dark:fill-light-blue h-4 w-4" />
                            )}
                            <span className="text-xs font-semibold text-gray-500">
                              Prior Cases ({priorCases.length})
                            </span>
                          </div>
                          {openPriorSections[priorKey] &&
                            priorCases.map(wrg => renderCaseRow(wrg, true))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </section>
          )
        }}
        pt={{
          root: {
            className: 'mt-8 no-expansion-space'
          },
          bodyRow: {
            className: 'table-row'
          },
          paginator: {
            root: {
              className: 'justify-end'
            },
            RPPDropdown: {
              root: {
                className: 'max-w-[120px]'
              }
            },
            prevPageButton: {
              className: 'paginator-prev-page-button'
            }
          }
        }}>
        <Column
          selectionMode="multiple"
          headerClassName="w-10 custom-checkbox"
          bodyClassName="hide-disabled-checkbox"
        />
        {columns.map(item => (
          <Column
            key={item.field ?? item.header}
            field={item.field}
            header={item.header}
            body={item.body}
            sortable={item.sortable}
            headerClassName={item.headerClassName}
            bodyClassName={item.bodyClassName}
          />
        ))}
        <Column
          body={rowData => (
            <div className="flex items-center justify-center">
              <IconButton
                title="View"
                onClick={() => navigate(`/submissions/${rowData.name}`)}
                severity="secondary"
                icon={<ViewIcon className="fill-white h-4 w-4" />}
              />
            </div>
          )}
          headerClassName="one-col-editor-header editor-header"
          headerStyle={{ width: '50px' }}
          pt={{ root: { className: 'px-2 py-2 text-center' } }}
        />
      </DataTable>
    </>
  )
}

export default RunView
